import React, { useState, useEffect } from "react";
import {
  Search, Filter, Download, ArrowUpDown, ChevronDown,
  DollarSign, ArrowUp, ArrowDown, FileText, Calendar,
  CreditCard, User, Building2, Plus, X, Eye, MoreVertical,
  Clock, CheckCircle, AlertCircle, Unlock, Lock, Timer
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { escrowAPI, Escrow, AdminTransactionsResponse, ClearingStatusEnum } from "../../services/escrowAPI";
import { GlobalService } from "../../modules";
import { PlatformFeeUtils, PLATFORM_CONFIG } from "../../config/platformConfig";
import MilestoneDialog from "../../components/ui/MilestoneDialog";
import ReceiptDialog from "../../components/ui/ReceiptDialog";

const TransactionsPage = () => {
  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showTransactionDetails, setShowTransactionDetails] = useState(null);
  const [dateRange, setDateRange] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState<Escrow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    totalVolume: 0,
    payments: 0,
    payouts: 0,
    platformFees: 0,
    totalCommission: 0,
    releasedTransactions: 0
  });
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedTransactionForReceipt, setSelectedTransactionForReceipt] = useState<Escrow | null>(null);

  // Fetch transactions data
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams: any = {
        page: currentPage,
        limit: 10,
        sort: [{ orderBy: "createdAt", order: sortBy === "newest" ? "DESC" : "ASC" }],
        filters: {}
      };

      // Add filters
      if (selectedStatus !== "all") {
        queryParams.filters.status = selectedStatus;
      }
      if (searchQuery) {
        // Note: search might not be supported in the current interface
        // We'll need to filter on the frontend for now
      }

      const response = await escrowAPI.getAdminTransactions(queryParams);
      console.log("Admin transactions response:", response);

      if (response && response.transactions) {
        let filteredData = response.transactions;

        // Frontend search filtering
        if (searchQuery) {
          filteredData = response.transactions.filter((escrow: Escrow) =>
            escrow.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            escrow.client?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            escrow.client?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            escrow.professional?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            escrow.professional?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            escrow.milestone?.description?.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        setTransactions(filteredData);
        setTotalPages(Math.ceil((response.totalCount || 0) / 10));
        setTotalCount(response.totalCount || 0);

        // Set stats from API response
        setStats({
          totalVolume: response.totalVolume || 0,
          payments: response.totalPayments || 0,
          payouts: 0, // Not provided by API
          platformFees: response.totalPlatformFees || 0,
          totalCommission: response.totalCommission || 0,
          releasedTransactions: response.releasedTransactions || 0
        });
      } else {
        setTransactions([]);
        setTotalPages(1);
        setTotalCount(0);
        setStats({
          totalVolume: 0,
          payments: 0,
          payouts: 0,
          platformFees: 0,
          totalCommission: 0,
          releasedTransactions: 0
        });
      }
    } catch (err: any) {
      console.error("Error fetching transactions:", err);
      setError(err.message || "Failed to fetch transactions");
      GlobalService.showToast("error", "Error", "Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };


  // Load transactions on component mount and when filters change
  useEffect(() => {
    fetchTransactions();
  }, [currentPage, sortBy, selectedStatus, searchQuery]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== "") {
        fetchTransactions();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Helper functions
  const getStatusBadge = (status: any) => {
    const statusName = typeof status === "string" ? status : status?.name || "unknown";
    const styles = {
      completed: "bg-green-100 text-green-700",
      paid: "bg-green-100 text-green-700",
      funded: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      created: "bg-yellow-100 text-yellow-700",
      failed: "bg-red-100 text-red-700",
      cancelled: "bg-red-100 text-red-700",
      released: "bg-blue-100 text-blue-700"
    };
    return styles[statusName.toLowerCase()] || "bg-gray-100 text-gray-700";
  };

  const getStatusText = (status: any) => {
    const statusName = typeof status === "string" ? status : status?.name || "unknown";
    return statusName.charAt(0).toUpperCase() + statusName.slice(1).toLowerCase();
  };

  const getCurrencyDisplay = (currency: any) => {
    if (typeof currency === "string") return currency;
    return currency?.id || currency?.code || "USD";
  };

  // Calculate hours remaining in clearing window
  const calculateClearingHoursRemaining = (transaction: Escrow): number | null => {
    // Use actualReleaseDate (when payment was released) for accurate countdown
    const releaseDate = transaction.actualReleaseDate || transaction.updatedAt;
    if (!releaseDate) return null;
    
    const now = new Date();
    const released = new Date(releaseDate);
    const hoursSinceRelease = (now.getTime() - released.getTime()) / (1000 * 60 * 60);
    const hoursRemaining = 72 - hoursSinceRelease;
    
    return Math.max(0, hoursRemaining);
  };

  // Get clearing status display
  const getClearingStatusDisplay = (clearingStatus: any) => {
    if (!clearingStatus) return null;
    
    const statusId = typeof clearingStatus === "number" ? clearingStatus : clearingStatus?.id;
    const statusMap: Record<number, { label: string; color: string }> = {
      1: { label: "Clearing", color: "bg-yellow-100 text-yellow-700" },
      2: { label: "Released", color: "bg-green-100 text-green-700" },
      3: { label: "On Hold", color: "bg-red-100 text-red-700" },
      4: { label: "Auto-Cleared", color: "bg-blue-100 text-blue-700" },
    };
    
    return statusMap[statusId] || null;
  };

  // Handle manual release
  const handleManualRelease = async (transactionId: string) => {
    try {
      await escrowAPI.manualReleaseFunds(transactionId);
      GlobalService.showToast("success", "Success", "Funds released successfully. Professional can now withdraw.");
      fetchTransactions(); // Refresh the list
      
      // Close the modal if it's open
      if (showTransactionDetails) {
        setShowTransactionDetails(null);
      }
    } catch (error: any) {
      console.error("Error releasing funds:", error);
      GlobalService.showToast("error", "Error", error.message || "Failed to release funds");
    }
  };

  // Handle hold funds
  const handleHoldFunds = async (transactionId: string) => {
    try {
      await escrowAPI.holdFunds(transactionId);
      GlobalService.showToast("success", "Success", "Funds placed on hold for review");
      fetchTransactions(); // Refresh the list
      
      // Close the modal if it's open
      if (showTransactionDetails) {
        setShowTransactionDetails(null);
      }
    } catch (error: any) {
      console.error("Error holding funds:", error);
      GlobalService.showToast("error", "Error", error.message || "Failed to hold funds");
    }
  };

  // Transaction Details Modal Component
  const TransactionDetailsModal = ({ transaction, isOpen, onClose }) => {
    if (!transaction || !isOpen) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="p-6 border-b" style={{ textAlign: "left" }}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge variant={undefined} className={getStatusBadge(transaction.status)}>
                      {getStatusText(transaction.status).toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="border-green-200 text-green-700">
                      ESCROW
                    </Badge>
                  </div>
                  <h2 className="text-xl font-semibold mt-2">{transaction.id}</h2>
                  <p className="text-sm text-gray-500">
                    {transaction.milestone?.description || "Escrow Payment"}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6" style={{ textAlign: "left" }}>
              {/* Amount Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Amount Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Total Amount (Including Fees)</span>
                      <p className="text-2xl font-bold text-gray-900">
                        {transaction.currency.initial} {(transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(transaction.amount || 0)).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Status</span>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusText(transaction.status).toLowerCase() === "completed" ||
                          getStatusText(transaction.status).toLowerCase() === "paid" ||
                          getStatusText(transaction.status).toLowerCase() === "funded" ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : getStatusText(transaction.status).toLowerCase() === "pending" ||
                          getStatusText(transaction.status).toLowerCase() === "created" ? (
                          <Clock className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="capitalize">{getStatusText(transaction.status)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clearing Status Information - Only show for released status */}
              {getStatusText(transaction.status).toLowerCase() === "released" && (
                <div className="space-y-4">
                  <h3 className="font-medium">Clearing Status</h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="space-y-3">
                      {transaction.clearingStatus && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Status</span>
                          <Badge variant={undefined} className={getClearingStatusDisplay(transaction.clearingStatus)?.color}>
                            {getClearingStatusDisplay(transaction.clearingStatus)?.label}
                          </Badge>
                        </div>
                      )}
                      
                      {(transaction.clearingStatus?.id === 1 || !transaction.clearingStatus) && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Time Remaining</span>
                            <div className="flex items-center gap-2">
                              <Timer className="h-4 w-4 text-yellow-600" />
                              <span className="font-medium text-yellow-700">
                                {calculateClearingHoursRemaining(transaction)?.toFixed(1)} hours
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            Funds will be automatically available after 72 hours from release
                          </div>
                        </>
                      )}

                      {transaction.clearingStatus?.id === 2 && transaction.clearingReleasedAt && (
                        <div className="text-sm text-green-700">
                          Manually released on {new Date(transaction.clearingReleasedAt).toLocaleString()}
                        </div>
                      )}

                      {transaction.clearingStatus?.id === 3 && (
                        <div className="text-sm text-red-700 flex items-center gap-2">
                          <Lock className="h-4 w-4" />
                          Funds are on hold for review
                        </div>
                      )}

                      {/* Admin Actions */}
                      <div className="flex gap-2 pt-2 border-t">
                        {(transaction.clearingStatus?.id === 1 || !transaction.clearingStatus || transaction.clearingStatus?.id === 3) && (
                          <Button
                            size="sm"
                            onClick={() => handleManualRelease(transaction.id)}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                          >
                            <Unlock className="h-4 w-4" />
                            Release Immediately
                          </Button>
                        )}
                        
                        {(transaction.clearingStatus?.id === 1 || !transaction.clearingStatus) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleHoldFunds(transaction.id)}
                            className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Lock className="h-4 w-4" />
                            Hold for Review
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Parties Involved */}
              <div className="space-y-4">
                <h3 className="font-medium">Transaction Parties</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Client</h4>
                    <div className="space-y-2">
                      <p className="font-medium">
                        {transaction.client?.firstName} {transaction.client?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{transaction.client?.email}</p>
                      <Badge variant="secondary">
                        Client
                      </Badge>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Professional</h4>
                    <div className="space-y-2">
                      <p className="font-medium">
                        {transaction.professional?.firstName} {transaction.professional?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{transaction.professional?.email}</p>
                      <Badge variant="secondary">
                        Professional
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-4">
                <h3 className="font-medium">Payment Method</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="font-medium">
                        {transaction.paymentProvider === "stripe" ? "Stripe" : "Payment Gateway"}
                      </p>
                      <p className="text-sm text-gray-500">
                        Payment ID: {transaction.stripePaymentIntentId || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fees Breakdown */}
              <div className="space-y-4">
                <h3 className="font-medium">Fees Breakdown</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Milestone Amount</span>
                      <span className="font-medium">
                        {transaction.currency.initial} {(transaction.amount || 0).toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Tracker Amount - show only if greater than 0 */}
                    {(transaction.trackerAmount || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tracker Amount</span>
                        <span className="font-medium">
                          {transaction.currency.initial} {(transaction.trackerAmount || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    {/* Emergency Cost - show only if greater than 0 */}
                    {(transaction.emergencyCost || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Emergency Cost</span>
                        <span className="font-medium">
                          {transaction.currency.initial} {(transaction.emergencyCost || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">{PLATFORM_CONFIG.PLATFORM_FEE_LABEL} ({PlatformFeeUtils.getPlatformFeePercentageDisplay()})</span>
                      <span className="font-medium">
                        {transaction.currency.initial} {(transaction.platformFee || PlatformFeeUtils.calculatePlatformFee(transaction.amount || 0)).toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="pt-2 mt-2 border-t flex justify-between">
                      <span className="font-medium">Total Amount</span>
                      <span className="font-medium">
                        {transaction.currency.initial} {(transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(transaction.amount || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Commission Section - show only for funded or released status */}
              {(getStatusText(transaction.status).toLowerCase() === "funded" || 
                getStatusText(transaction.status).toLowerCase() === "released") && (
                <div className="space-y-4">
                  <h3 className="font-medium">Commission Details</h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Amount</span>
                        <span className="font-medium">
                          {transaction.currency.initial} {(transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(transaction.amount || 0)).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Withdrawal Amount</span>
                        <span className="font-medium">
                          {transaction.currency.initial} {(transaction.withdrawlAmount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2 mt-2 border-t border-blue-300 flex justify-between">
                        <span className="font-medium text-blue-700">Commission</span>
                        <span className="font-bold text-blue-700">
                          {transaction.currency.initial} {((transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(transaction.amount || 0)) - (transaction.withdrawlAmount || 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Additional Information</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Created Date</span>
                      <p className="font-medium">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Updated Date</span>
                      <p className="font-medium">
                        {new Date(transaction.updatedAt).toLocaleString()}
                      </p>
                    </div>
                    {transaction.contract?.id && (
                      <div>
                        <span className="text-sm text-gray-500">Contract ID</span>
                        <p className="font-medium">{transaction.contract.id}</p>
                      </div>
                    )}
                    {transaction.milestone?.id && (
                      <div>
                        <span className="text-sm text-gray-500">Milestone ID</span>
                        <p className="font-medium">{transaction.milestone.id}</p>
                      </div>
                    )}
                    {transaction.notes && (
                      <div className="col-span-2">
                        <span className="text-sm text-gray-500">Notes</span>
                        <p className="font-medium">{transaction.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedTransactionForReceipt(transaction);
                    setShowReceiptDialog(true);
                  }}
                >
                  Download Receipt
                </Button>
                <Button 
                  onClick={() => {
                    if (transaction.contract?.id) {
                      setSelectedContractId(transaction.contract.id);
                      setShowMilestoneDialog(true);
                    } else {
                      GlobalService.showToast("warning", "Warning", "No related contract found for this transaction");
                    }
                  }}
                >
                  View Related Order
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
                <p className="text-gray-600 mt-1">Monitor and manage all financial transactions</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  New Transaction
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Volume (Incl. Fees)</p>
                    <p className="text-2xl font-semibold">${stats.totalVolume.toFixed(2)}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Milestone Amounts</p>
                    <p className="text-2xl font-semibold">${stats.payments.toFixed(2)}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ArrowDown className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Transactions</p>
                    <p className="text-2xl font-semibold">{totalCount}</p>
                    <p className="text-xs text-gray-400">{stats.releasedTransactions} released</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">{PLATFORM_CONFIG.PLATFORM_FEE_LABEL} ({PlatformFeeUtils.getPlatformFeePercentageDisplay()})</p>
                    <p className="text-2xl font-semibold">${stats.platformFees.toFixed(2)}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FileText className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Commission</p>
                    <p className="text-2xl font-semibold">${stats.totalCommission.toFixed(2)}</p>
                    <p className="text-xs text-gray-400">From released transactions</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="quarter">This Quarter</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="amount_high">Highest Amount</option>
                    <option value="amount_low">Lowest Amount</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transaction Type
                      </label>
                      <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Types</option>
                        <option value="payment">Payments</option>
                        <option value="payout">Payouts</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Status</option>
                        <option value="paid">Paid</option>
                        <option value="funded">Funded</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="created">Created</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="released">Released</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method
                      </label>
                      <select
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Methods</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Transactions Table */}
            <Card className="overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading transactions...</span>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-600 mb-4">{error}</p>
                  <Button onClick={fetchTransactions} variant="outline">
                    Try Again
                  </Button>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No transactions found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Transaction
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Professional
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clearing
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="font-medium text-gray-900">{transaction.id}</div>
                                <div className="text-sm text-gray-500">
                                  {transaction.milestone?.description || "Escrow Payment"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {transaction.client?.firstName} {transaction.client?.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{transaction.client?.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {transaction.professional?.firstName} {transaction.professional?.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{transaction.professional?.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {transaction.currency.initial} {(transaction.totalAmount || PlatformFeeUtils.calculateTotalWithFee(transaction.amount || 0)).toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Milestone: {transaction.currency.initial} {(transaction.amount || 0).toFixed(2)} + Fee: {transaction.currency.initial} {(transaction.platformFee || PlatformFeeUtils.calculatePlatformFee(transaction.amount || 0)).toFixed(2)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={undefined} className={getStatusBadge(transaction.status)}>
                              {getStatusText(transaction.status).toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusText(transaction.status).toLowerCase() === "released" ? (
                              <div className="flex flex-col gap-1">
                                {transaction.clearingStatus && (
                                  <Badge variant={undefined} className={getClearingStatusDisplay(transaction.clearingStatus)?.color}>
                                    {getClearingStatusDisplay(transaction.clearingStatus)?.label}
                                  </Badge>
                                )}
                                {(transaction.clearingStatus?.id === 1 || !transaction.clearingStatus) && (
                                  <span className="text-xs text-gray-500">
                                    {calculateClearingHoursRemaining(transaction)?.toFixed(0)}h left
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedTransactionForReceipt(transaction);
                                  setShowReceiptDialog(true);
                                }}
                                className="text-green-600 hover:text-green-900"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Receipt
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowTransactionDetails(transaction)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                View Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {transactions.length > 0 && (
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">
                      Showing page {currentPage} of {totalPages} ({totalCount} total transactions)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1 || loading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        transaction={showTransactionDetails}
        isOpen={!!showTransactionDetails}
        onClose={() => setShowTransactionDetails(null)}
      />

      {/* Milestone Dialog */}
      {selectedContractId && (
        <MilestoneDialog
          isOpen={showMilestoneDialog}
          onClose={() => {
            setShowMilestoneDialog(false);
            setSelectedContractId(null);
          }}
          contractId={selectedContractId}
        />
      )}

      {/* Receipt Dialog */}
      <ReceiptDialog
        isOpen={showReceiptDialog}
        onClose={() => {
          setShowReceiptDialog(false);
          setSelectedTransactionForReceipt(null);
        }}
        transaction={selectedTransactionForReceipt}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default TransactionsPage;